/**
 * ProtectedRoute — Wrapper that redirects unauthenticated users to login
 *
 * Usage:
 *   <ProtectedRoute>
 *       <DashboardPage />
 *   </ProtectedRoute>
 *
 * If the user is not authenticated, renders the LoginPage instead.
 * If auth is still loading, shows a simple loading indicator.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import LoginPage from './LoginPage';
import SetupAccountPage from './SetupAccountPage';
import PasskeySetup from './PasskeySetup';
import TwoFactorGate from './TwoFactorGate';
import * as accountApi from '../utils/accountApi';

// Inject spinner keyframes once (inline styles can't define @keyframes)
const SPINNER_STYLE_ID = 'protected-route-spinner';
function injectSpinnerStyle() {
    if (typeof document === 'undefined') return;
    if (document.getElementById(SPINNER_STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = SPINNER_STYLE_ID;
    style.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
    document.head.appendChild(style);
}

export default function ProtectedRoute({ children }) {
    const { user, loading, refreshUser, isAuthenticated } = useAuth();
    const params = new URLSearchParams(window.location.search);
    const inviteToken = params.get('invite') || params.get('token');
    const [passkeySkipped, setPasskeySkipped] = useState(false);
    // Set only once Hanko confirms a registered authenticator — never by the
    // member choosing to move on. It is not a skip.
    const [twoFactorSatisfied, setTwoFactorSatisfied] = useState(false);

    useEffect(() => { injectSpinnerStyle(); }, []);

    // Check if passkey setup is required
    const needsPasskey = useCallback(() => {
        if (passkeySkipped || !user?.preferences) return false;
        const prefs = typeof user.preferences === 'string' ? JSON.parse(user.preferences) : user.preferences;
        return !!prefs.passkey_required;
    }, [user, passkeySkipped]);

    const handlePasskeyComplete = useCallback(async () => {
        // Clear the passkey_required flag
        try {
            const prefs = typeof user.preferences === 'string' ? JSON.parse(user.preferences) : (user.preferences || {});
            await accountApi.updateUserPreferences(user.user_id, { ...prefs, passkey_required: false });
            await refreshUser();
        } catch {
            // Proceed even if flag clear fails
        }
    }, [user, refreshUser]);

    // An administrator can require a member to hold a second factor, but cannot
    // register one for them — the secret has to reach that person's own phone.
    // The requirement is therefore enforced here, at the member's next visit.
    const needsTwoFactor = useCallback(() => {
        if (twoFactorSatisfied || !user?.preferences) return false;
        try {
            const prefs = typeof user.preferences === 'string' ? JSON.parse(user.preferences) : user.preferences;
            return !!prefs.mfa_required;
        } catch {
            return false;
        }
    }, [user, twoFactorSatisfied]);

    // Clearing the flag is what releases the gate for good. It is cleared once
    // Hanko confirms a registered authenticator — including for someone who had
    // already enrolled before the requirement was set, so the requirement
    // resolves itself rather than trapping a member who has already complied.
    const handleTwoFactorSatisfied = useCallback(async () => {
        // Release this session before touching the server. The member now holds
        // a second factor, so a failed preference write must not strand them
        // behind a gate they have already satisfied.
        setTwoFactorSatisfied(true);
        try {
            const prefs = typeof user.preferences === 'string' ? JSON.parse(user.preferences) : (user.preferences || {});
            await accountApi.updateUserPreferences(user.user_id, { ...prefs, mfa_required: false });
        } catch {
            // Fall through to refresh regardless; the gate re-checks against
            // Hanko on the next render and will not ask twice for a factor
            // that is now registered.
        }
        await refreshUser();
    }, [user, refreshUser]);

    // Still checking session — show loading state
    if (loading) {
        return (
            <div style={styles.loading}>
                <div style={styles.spinner} />
                <p style={styles.loadingText}>Loading...</p>
            </div>
        );
    }

    // Invite flow: show setup wizard instead of login
    if (inviteToken && !isAuthenticated) {
        return <SetupAccountPage token={inviteToken} onComplete={refreshUser} />;
    }

    // Not authenticated — show login page
    if (!isAuthenticated) {
        return <LoginPage onLoginSuccess={refreshUser} />;
    }

    // Two-factor required by an administrator — enroll before anything else.
    // This sits ahead of the passkey gate because a passkey skips the password
    // path entirely, and skipping it must not also skip the second factor the
    // administrator asked for.
    if (needsTwoFactor()) {
        return <TwoFactorGate onSatisfied={handleTwoFactorSatisfied} />;
    }

    // Passkey setup required — show passkey registration
    if (needsPasskey()) {
        return (
            <div style={styles.passkeyGate}>
                <div style={styles.passkeyCard}>
                    <h2 style={styles.passkeyTitle}>🔑 Passkey Setup Required</h2>
                    <p style={styles.passkeyDesc}>Your administrator has enabled passkey login for your account. Please register a passkey to continue.</p>
                    <PasskeySetup onSuccess={handlePasskeyComplete} />
                    <button onClick={() => setPasskeySkipped(true)} style={styles.skipBtn}>
                        Skip for now
                    </button>
                </div>
            </div>
        );
    }

    // Authenticated — render children
    return children;
}

const styles = {
    loading: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f3f4f6',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    loadingText: {
        color: '#6b7280',
        fontSize: '14px',
        marginTop: '12px',
    },
    spinner: {
        width: '32px',
        height: '32px',
        border: '3px solid #e5e7eb',
        borderTopColor: '#2563eb',
        borderRadius: '50%',
        animation: 'spin 0.6s linear infinite',
    },
    passkeyGate: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#0F172A',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    passkeyCard: {
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 16,
        padding: '40px 36px',
        maxWidth: 440,
        width: '100%',
        textAlign: 'center',
    },
    passkeyTitle: {
        color: '#F1F5F9',
        fontSize: 22,
        fontWeight: 700,
        margin: '0 0 12px',
    },
    passkeyDesc: {
        color: '#94A3B8',
        fontSize: 14,
        lineHeight: 1.5,
        marginBottom: 24,
    },
    skipBtn: {
        marginTop: 16,
        padding: '8px 20px',
        background: 'transparent',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 8,
        color: '#64748B',
        fontSize: 13,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
    },
};
